import BetaFeedback from "./BetaFeedback";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link, Navigate, useParams, useLocation } from "react-router-dom";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import {
  reviewApi,
  ReviewApiError,
  reviewStatusLabel,
  customerStatusLabel,
  jurisdictionLabel,
  dateLabel,
  isOverdue,
  followUpEnded,
  nextActionFor,
  nextActionLabel,
  nextActionTone,
  topicLabel,
  shortDate,
  initials,
} from "./api";
import "./reviews.css";
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  FileText,
  MapPin,
  RefreshCw,
  Send,
} from "lucide-react";
import SubmittedRecord, { submittedItems } from "./SubmittedRecord";
import AttorneyWorkspace, {
  AttorneyWorkspaceHandle,
  WorkTab,
} from "./AttorneyWorkspace";
import ReviewProposal from "./ReviewProposal";
import ReviewMarkdown from "./ReviewMarkdown";
import AnswerRichEditor, {
  AnswerEditorHandle,
  Citation,
} from "./AnswerRichEditor";
import { lintAnswer } from "./answerMarkdown";
import ReviewLifecycle from "./ReviewLifecycle";
import DocumentRequests, { DocumentRequest } from "./DocumentRequests";
import { exportAnswer } from "./answerExport";
import { useDraftSafety, readRecovery, draftKey } from "./useDraftSafety";
import { customerCanMessage } from "./customerWorkflow";
import { useMessageDraft } from "./useMessageDraft";
/**
 * One attorney review. Attorneys open it at /reviews/:id; customers see it as
 * the Attorney review tab of their question (`embedded`), and a direct link
 * sends them there.
 */
export default function ReviewDetail({
  reviewId,
  embedded = false,
  onBackToChat,
  onReviewLoaded,
  onReviewChanged,
}: {
  reviewId?: string;
  embedded?: boolean;
  onBackToChat?: () => void;
  onReviewLoaded?: () => void;
  onReviewChanged?: (review: any) => void;
} = {}) {
  const params = useParams();
  const id = reviewId ?? params.id;
  const location = useLocation();
  const [recoveryConflict, setRecoveryConflict] = useState<any>(null);
  const [data, setData] = useState<any>(null),
    [messages, setMessages] = useState<any[]>([]),
    [reminderNotice, setReminderNotice] = useState(""),
    [draft, setDraft] = useState(""),
    [draftVersion, setDraftVersion] = useState(0),
    [savedDraft, setSavedDraft] = useState(""),
    [tab, setTab] = useState("discussion"),
    [loading, setLoading] = useState(true),
    [pendingAction, setPendingAction] = useState<string | null>(null),
    [error, setError] = useState(""),
    [success, setSuccess] = useState(""),
    [showDelivery, setShowDelivery] = useState(false),
    [workTab, setWorkTab] = useState<WorkTab | null>(null),
    [proposalRequest, setProposalRequest] = useState(0),
    [pinned, setPinned] = useState<Citation | null>(null),
    [hasMore, setHasMore] = useState(false);
  const busy = pendingAction !== null;
  const messageDraft = useMessageDraft(id, data);
  const body = messageDraft.body;
  const onReviewChangedRef = useRef(onReviewChanged);
  onReviewChangedRef.current = onReviewChanged;
  const messagesRef = useRef<any[]>([]);
  const messageViewport = useRef<HTMLDivElement>(null);
  const answerEditor = useRef<AnswerEditorHandle>(null);
  const attorneyWorkspace = useRef<AttorneyWorkspaceHandle>(null);
  const followMessages = useRef(true);
  useLayoutEffect(() => {
    const viewport = messageViewport.current;
    if (data?.viewer === "attorney" && viewport && followMessages.current)
      viewport.scrollTop = viewport.scrollHeight;
  }, [messages, data?.viewer, loading]);
  const revoked = useCallback((e: any) => {
    if (
      e instanceof ReviewApiError &&
      [401, 403, 404, 503].includes(e.status)
    ) {
      setData(null);
      setMessages([]);
      messagesRef.current = [];
      setDraft("");
      setSavedDraft("");
    }
    setError(e.message);
  }, []);
  const mergeMessages = useCallback((incoming: any[]) => {
    messagesRef.current = Array.from(
      new Map(
        [...messagesRef.current, ...incoming].map(m => [m.seq, m])
      ).values()
    ).sort((a, b) => Number(a.seq) - Number(b.seq));
    setMessages(messagesRef.current);
  }, []);
  const draftLoaded = useRef(false),
    messageKey = useRef(crypto.randomUUID()),
    deliveryKey = useRef(crypto.randomUUID());
  const onReviewLoadedRef = useRef(onReviewLoaded);
  onReviewLoadedRef.current = onReviewLoaded;
  const lastReviewSummary = useRef("");
  const load = useCallback(
    async (signal?: AbortSignal, refreshDraft = false) => {
      const result = await reviewApi(`/${id}`, "GET", undefined, signal);
      if (signal?.aborted) return;
      const summary = JSON.stringify([
        id,
        result.review.status,
        result.review.closed_at,
        result.review.proposal_state,
        result.review.payment_state,
        result.review.latest_attorney_seq,
        result.review.client_replied_through,
      ]);
      if (summary !== lastReviewSummary.current) {
        lastReviewSummary.current = summary;
        onReviewLoadedRef.current?.();
      }
      const previous = messagesRef.current;
      const next = previous.length
        ? await reviewApi(
            `/${id}/messages?after=${previous[previous.length - 1].seq}`,
            "GET",
            undefined,
            signal
          )
        : { messages: result.messages, hasMore: result.hasMoreMessages };
      if (signal?.aborted) return;
      setData(result);
      onReviewChangedRef.current?.(result.review);
      // Loading the review marks its updates read; refresh the Updates badge.
      window.dispatchEvent(new Event("review-notifications-changed"));
      mergeMessages(next.messages);
      setHasMore(next.hasMore);
      if (!draftLoaded.current || refreshDraft) {
        const recovery =
          !refreshDraft &&
          result.viewer === "attorney" &&
          !result.review.closed_at
            ? readRecovery(draftKey(id!, result.review.assigned_attorney_id))
            : null;
        setDraft(recovery?.body ?? result.draft?.body ?? "");
        if (recovery && recovery.body !== result.draft?.body) {
          setSuccess("Recovered your private draft from this browser session.");
          if (recovery.base !== (result.draft?.version || 0))
            setRecoveryConflict(result.draft || { body: "", version: 0 });
        }
        if (refreshDraft && result.viewer === "attorney") {
          try {
            sessionStorage.removeItem(
              draftKey(id!, result.review.assigned_attorney_id)
            );
          } catch {}
        }
        setSavedDraft(result.draft?.body || "");
        setDraftVersion(result.draft?.version || 0);
        draftLoaded.current = true;
      }
    },
    [id, mergeMessages]
  );
  useEffect(() => {
    const c = new AbortController();
    draftLoaded.current = false;
    setData(null);
    setMessages([]);
    messagesRef.current = [];
    followMessages.current = true;
    setDraft("");
    setSavedDraft("");
    setSuccess("");
    deliveryKey.current = crypto.randomUUID();
    setLoading(true);
    setError("");
    setReminderNotice("");
    messageKey.current = crypto.randomUUID();
    load(c.signal)
      .catch(e => {
        if (!c.signal.aborted) revoked(e);
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, [load, revoked]);
  // Stay current without a Refresh button: poll, and reload on returning.
  useEffect(() => {
    const c = new AbortController();
    const refresh = () => {
      if (document.visibilityState === "visible")
        load(c.signal).catch(e => {
          if (!c.signal.aborted) revoked(e);
        });
    };
    const t = setInterval(refresh, 20000);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", refresh);
      c.abort();
    };
  }, [load, revoked]);
  const safety = useDraftSafety({
    id,
    data,
    draft,
    saved: savedDraft,
    version: draftVersion,
    setDraft,
    setSaved: setSavedDraft,
    setVersion: setDraftVersion,
    onAccessError: revoked,
  });
  const setDraftConflict = safety.setConflict;
  useEffect(() => {
    if (recoveryConflict) {
      setDraftConflict(recoveryConflict);
      setRecoveryConflict(null);
    }
  }, [recoveryConflict, setDraftConflict]);
  const loadedReviewId = data?.review?.id;
  const sectionNavigation = useRef("");
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Apply the notification's section once, rather than every time messages change.
    // Message pagination below can still reveal an exact notification target.
    const navigation = `${id}:${location.search}:${loadedReviewId || ""}`;
    if (sectionNavigation.current !== navigation) {
      sectionNavigation.current = navigation;
      setTab(
        ["discussion", "snapshot", "answers"].includes(
          params.get("section") || ""
        )
          ? params.get("section")!
          : data?.answers?.length &&
              (data?.review?.status === "completed" || data?.review?.closed_at)
            ? "answers"
            : "discussion"
      );
    }
    if (loadedReviewId)
      requestAnimationFrame(() => {
        const el = document.getElementById(params.get("target") || "");
        if (el) {
          for (
            let ancestor = el.parentElement;
            ancestor;
            ancestor = ancestor.parentElement
          )
            if (ancestor instanceof HTMLDetailsElement) ancestor.open = true;
          el.scrollIntoView({ block: "center" });
        }
      });
  }, [
    id,
    location.search,
    loadedReviewId,
    messages.length,
    data?.answers?.length,
    data?.review?.status,
    data?.review?.closed_at,
  ]);
  // Load intervening discussion pages for an exact notification target. This
  // preserves the visible sequence instead of silently skipping unread messages.
  const targetLoadFailed = useRef("");
  useEffect(() => {
    const target = new URLSearchParams(location.search).get("target") || "";
    if (
      !target.startsWith("message-") ||
      !hasMore ||
      !messages.length ||
      messages.some(m => `message-${m.id}` === target) ||
      targetLoadFailed.current === target
    )
      return;
    const controller = new AbortController();
    reviewApi(
      `/${id}/messages?after=${messages[messages.length - 1].seq}`,
      "GET",
      undefined,
      controller.signal
    )
      .then(next => {
        if (controller.signal.aborted) return;
        mergeMessages(next.messages);
        setHasMore(next.hasMore);
      })
      .catch(e => {
        if (!controller.signal.aborted) {
          targetLoadFailed.current = target;
          revoked(e);
        }
      });
    return () => controller.abort();
  }, [id, location.search, hasMore, messages, mergeMessages, revoked]);
  async function action(fn: () => Promise<void>, kind = "refresh") {
    if (busy) return;
    setPendingAction(kind);
    setError("");
    setSuccess("");
    try {
      await fn();
    } catch (e: any) {
      revoked(e);
    } finally {
      setPendingAction(null);
    }
  }
  async function more() {
    await action(async () => {
      const last = messages[messages.length - 1]?.seq || 0;
      const result = await reviewApi(`/${id}/messages?after=${last}`);
      mergeMessages(result.messages);
      setHasMore(result.hasMore);
    });
  }
  if (loading)
    return (
      <main className="reviewed-documents" role="status">
        Opening review…
      </main>
    );
  if (!data)
    return (
      <main className="reviewed-documents">
        <Alert variant="danger">{error || "Review not found"}</Alert>
        <Link to="/reviews">Back to reviews</Link>
      </main>
    );
  const r = data.review,
    attorney = data.viewer === "attorney",
    dirty = draft !== savedDraft;
  // Customers read reviews on their question page; keep notification and
  // Stripe return parameters on the way there.
  if (!attorney && !embedded) {
    const next = new URLSearchParams(location.search);
    next.set("tab", "attorney");
    next.set("review", r.id);
    return (
      <Navigate replace to={`/questions/${r.question_id}?${next.toString()}`} />
    );
  }
  const canWork =
    !r.closed_at &&
    r.status !== "cancelled" &&
    !followUpEnded(r) &&
    (!r.proposal_state ||
      r.proposal_state === "legacy" ||
      (r.proposal_state === "agreed" &&
        ["in_progress", "completed"].includes(r.status)));
  const canDeliver =
    !busy &&
    !safety.saving &&
    !safety.conflict &&
    !dirty &&
    !!draft.trim() &&
    draftVersion > 0 &&
    canWork;
  const next = nextActionFor(r);
  const documentRequests: DocumentRequest[] = data.documentRequests || [];
  const addedFiles = documentRequests.flatMap(q => q.files);
  const ended = !!r.closed_at || r.status === "cancelled";
  const customerNow = Math.max(
    Date.now(),
    new Date(data.serverTime || 0).getTime()
  );
  // Revised answers and document requests belong to the follow-up window.
  const revisionAllowed = !ended && !followUpEnded(r);
  const lint = lintAnswer(savedDraft);
  const activeTab: WorkTab =
    workTab ?? (next === "reply" ? "messages" : "answer");
  const seenClient = Math.max(
      0,
      ...messages
        .filter(m => m.author_kind === "client")
        .map(m => Number(m.seq))
    ),
    seenAttorney = Math.max(
      0,
      ...messages
        .filter(m => m.author_kind === "attorney")
        .map(m => Number(m.seq))
    );
  const saveDraft = safety.save;
  async function send() {
    await action(async () => {
      await reviewApi(`/${id}/messages`, "POST", {
        body,
        requestKey: messageKey.current,
        respondsThrough: attorney ? seenClient : seenAttorney,
      });
      messageDraft.clear();
      setReminderNotice("");
      messageKey.current = crypto.randomUUID();
      followMessages.current = true;
      await load();
      setSuccess(
        attorney
          ? "Message sent to your client."
          : "Message sent to your attorney."
      );
    }, "send");
  }
  async function deliver() {
    await action(async () => {
      await reviewApi(`/${id}/deliver`, "POST", {
        requestKey: deliveryKey.current,
        draftVersion,
        respondsThrough: seenClient,
      });
      deliveryKey.current = crypto.randomUUID();
      setShowDelivery(false);
      await load(undefined, true);
      setSuccess("Answer delivered. The client has been notified in Reviewed.");
      setTab("answers");
    }, "deliver");
  }
  const answerArticle = (a: any) => (
    <article className="review-answer" key={a.id} id={`answer-${a.id}`}>
      <header>
        <strong>Answer version {a.answer_version}</strong>
        <span>
          {a.author_name} · {dateLabel(a.delivered_at)}
        </span>
      </header>
      <ReviewMarkdown>{a.body}</ReviewMarkdown>
      <p className="review-answer-notice">{a.notice_snapshot?.text || "This answer is limited to the submitted question and agreed review scope. AI chat is separate and is not legal advice. Consult the original review terms for the jurisdiction, timing, and follow-up allowance."}</p>
      <Button
        size="sm"
        variant="outline-dark"
        onClick={() => {
          const el = document.getElementById(`answer-${a.id}`)!;
          void exportAnswer(
            el,
            `${r.title} — Answer ${a.answer_version}`
          ).catch(e => setError(e.message));
        }}
      >
        Download answer (.docx)
      </Button>
    </article>
  );
  const discussionSection = (
    <section
      className={`review-section${attorney ? " attorney-discussion-content" : ""}`}
    >
      {!attorney && <h2>Messages with your attorney</h2>}
      {!attorney && (
        <p className="text-secondary">
          Answer your attorney’s questions and ask about their answer here.
          Their written answer is under Attorney’s answer.
        </p>
      )}
      <div
        className="review-messages"
        ref={messageViewport}
        tabIndex={attorney ? 0 : undefined}
        aria-label="Discussion messages"
        onScroll={e => {
          const el = e.currentTarget;
          followMessages.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < 32;
        }}
      >
        {!messages.length && (
          <p className="attorney-empty">
            {attorney
              ? "Ask your client for the facts or clarification you need to prepare your answer."
              : "Your conversation with your attorney will appear here."}
          </p>
        )}
        {messages.map(m => (
          <article
            className={`review-message ${m.author_kind}`}
            key={m.id}
            id={`message-${m.id}`}
          >
            <header>
              <span className="rv-message-avatar" aria-hidden="true">
                {initials(m.author_name)}
              </span>
              <strong>{m.author_name}</strong>
              <span>
                {m.author_kind === "attorney" ? "Attorney" : "Client"} ·{" "}
                {dateLabel(m.created_at)}
              </span>
            </header>
            <p>{m.body}</p>
          </article>
        ))}
        {hasMore && (
          <Button variant="light" disabled={busy} onClick={more}>
            Load more messages
          </Button>
        )}
      </div>
      <DocumentRequests
        reviewId={id!}
        requests={documentRequests}
        attorney={attorney}
        canRequest={
          attorney &&
          !ended &&
          revisionAllowed &&
          r.attorney_status === "approved"
        }
        canUpload={!ended}
        phase={
          !data.proposals?.length
            ? "legacy"
            : r.proposal_state === "agreed"
              ? "agreed"
              : "proposal"
        }
        onChanged={() => load()}
        onError={revoked}
        onViewTerms={
          data.proposals?.length
            ? () => setProposalRequest(n => n + 1)
            : undefined
        }
        onReminder={
          attorney &&
          !ended &&
          revisionAllowed &&
          r.assigned_attorney_id &&
          r.attorney_status === "approved"
            ? request => {
                if (body.trim()) {
                  setReminderNotice(
                    "Your unsent message is preserved. Send or clear it before writing a reminder."
                  );
                } else {
                  setReminderNotice("");
                  messageDraft.change(
                    `I'm following up on this document request: ${request.note}\n\nPlease send it so I can review the question you've submitted.`
                  );
                  messageKey.current = crypto.randomUUID();
                }
                attorneyWorkspace.current?.showMessages();
              }
            : undefined
        }
      />
      {r.closed_at || r.status === "cancelled" ? (
        <p className="p-3">
          This review has ended. Its discussion is read-only.
        </p>
      ) : !attorney &&
        r.status === "completed" &&
        !customerCanMessage(r, customerNow) ? (
        <Alert variant="info">
          The included follow-up has ended. Read your answer and messages, then
          close this review. Additional work needs a new review.
        </Alert>
      ) : r.assigned_attorney_id && r.attorney_status !== "approved" ? (
        <Alert variant="info">
          Discussion is paused while an admin reviews the attorney assignment.
        </Alert>
      ) : r.assigned_attorney_id ? (
        <Form
          className={`review-composer${attorney ? "" : " mt-4"}`}
          onSubmit={e => {
            e.preventDefault();
            void send();
          }}
        >
          {reminderNotice && (
            <p className="mb-2" role="status">
              {reminderNotice}
            </p>
          )}
          <Form.Group controlId="review-reply">
            <Form.Label>
              {attorney
                ? seenClient > 0
                  ? "Reply to the client"
                  : "Message the client"
                : "Message your attorney"}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={attorney ? 2 : 4}
              value={body}
              disabled={busy || r.status === "cancelled"}
              maxLength={12000}
              onChange={e => {
                messageDraft.change(e.target.value);
                setReminderNotice("");
                messageKey.current = crypto.randomUUID();
              }}
            />
          </Form.Group>
          {body && (
            <small
              role="status"
              className={messageDraft.saved ? "text-secondary" : "text-danger"}
            >
              {messageDraft.saved
                ? "Draft saved in this browser tab · not sent"
                : "Draft could not be saved. Copy it before leaving."}
            </small>
          )}
          <div className="review-composer-footer">
            <span>
              {attorney
                ? "Shared with your client"
                : "Sent to your assigned attorney"}
            </span>
            <Button
              type="submit"
              className="mt-3"
              disabled={busy || !body.trim() || r.status === "cancelled"}
            >
              <Send size={12} />{" "}
              {pendingAction === "send" ? "Sending…" : "Send message"}
            </Button>
          </div>
        </Form>
      ) : (
        <Alert variant="info">
          You can message your attorney once one is matched to your request.
        </Alert>
      )}
    </section>
  );
  const snapshotSection = (
    <SubmittedRecord
      reviewId={id!}
      snapshot={data.snapshot}
      hash={r.snapshot_hash}
      embedded={attorney}
      clientView={!attorney}
      initialDocumentId={
        new URLSearchParams(location.search).get("document") || undefined
      }
      onQuote={
        attorney && canWork
          ? (text, source) => {
              setWorkTab("answer");
              answerEditor.current?.insertCitation({ text, source });
            }
          : undefined
      }
      onPin={attorney ? setPinned : undefined}
      addedFiles={addedFiles}
      onAccessError={revoked}
    />
  );
  const answersSection = (
    <section className="review-section">
      {!attorney && <h2>Your attorney’s written answers</h2>}
      {!data.answers.length ? (
        <p className="attorney-empty">
          {ended
            ? "No answer was delivered for this review."
            : attorney
              ? "Your final written answer will appear here for you and your client once delivered."
              : "Your attorney’s written answer to the agreed request will appear here once delivered."}
        </p>
      ) : attorney ? (
        <>
          {answerArticle(data.answers[data.answers.length - 1])}
          {data.answers.length > 1 && (
            <details className="attorney-answer-history">
              <summary>Earlier answers ({data.answers.length - 1})</summary>
              {data.answers.slice(0, -1).reverse().map(answerArticle)}
            </details>
          )}
        </>
      ) : (
        data.answers.map(answerArticle)
      )}
    </section>
  );
  const draftSection = (
    <section className="review-section attorney-draft-content">
      {!canWork && (
        <p className="p-3 mb-0" role="status">
          {r.closed_at || r.status === "cancelled"
            ? "This review has ended. Delivered answers remain available below."
            : followUpEnded(r)
              ? "The follow-up window has ended. Delivered answers remain available; close the review when finished."
              : "Agree on the proposal before drafting or delivering an answer. You can read the submitted record and discuss the scope now."}
        </p>
      )}
      {safety.conflict && (
        <Alert variant="warning">
          <strong>Another saved version is available.</strong>
          <p>
            Your recovered text remains in the editor. Compare it with the saved
            version before choosing.
          </p>
          <details>
            <summary>View saved version</summary>
            <ReviewMarkdown>
              {safety.conflict.body || "Empty draft"}
            </ReviewMarkdown>
          </details>
          <Button variant="outline-dark" onClick={() => safety.resolve(true)}>
            Keep my text and save
          </Button>{" "}
          <Button variant="light" onClick={() => safety.resolve(false)}>
            Use saved version
          </Button>
        </Alert>
      )}
      <AnswerRichEditor
        ref={answerEditor}
        value={draft}
        onChange={safety.change}
        disabled={busy || !canWork}
        pinned={pinned}
        onSave={() => {
          if (dirty && !safety.saving && !safety.conflict) void saveDraft();
        }}
      />
      <div className="attorney-draft-footer">
        <span className="attorney-save-state" role="status">
          {safety.state ||
            (dirty
              ? "Unsaved changes"
              : draftVersion > 0
                ? "Saved · only you can see this"
                : "Only you can see this draft")}
        </span>

        {data.answers.length > 0 && revisionAllowed && (
          <Button disabled={!canDeliver} onClick={() => setShowDelivery(true)}>
            Deliver revised answer
          </Button>
        )}

        <small>Saves automatically. Press ⌘S or Ctrl+S to save now.</small>
      </div>
    </section>
  );
  const workflowControls = (
    <>
      <ReviewLifecycle
        review={r}
        attorney={attorney}
        customerActions={{
          proposal: () => setProposalRequest(n => n + 1),
          discussion: () => setTab("discussion"),
          answers: () => setTab("answers"),
        }}
        now={customerNow}
        actions={
          attorney
            ? {
                openTerms: data.proposals?.length
                  ? () => setProposalRequest(n => n + 1)
                  : undefined,
                start: () =>
                  action(async () => {
                    await reviewApi(`/${id}/start`, "POST", {
                      version: r.version,
                    });
                    await load();
                  }),
                deliver: () => setShowDelivery(true),
                canDeliver,
                showMessages: () => attorneyWorkspace.current?.showMessages(),
              }
            : undefined
        }
        onChanged={() => load()}
        onError={revoked}
        clientSeq={seenClient}
        attorneySeq={seenAttorney}
      />
      {!attorney && ended && data.snapshot?.beta && <BetaFeedback id={r.id} initial={data.feedback} />}
      <ReviewProposal
        customerBanner={!attorney}
        hideBar={attorney}
        openRequest={proposalRequest}
        openTarget={
          new URLSearchParams(location.search).get("section") === "proposal"
            ? new URLSearchParams(location.search).get("target") || "latest"
            : undefined
        }
        review={r}
        parties={data.snapshot?.beta?.parties || ""}
        proposals={data.proposals || []}
        decisions={data.proposalDecisions || []}
        savedCard={data.savedCard}
        payments={data.payments || []}
        attorney={attorney}
        onChanged={() => load()}
        onAccessError={revoked}
      />
    </>
  );
  return (
    <main
      className={`reviewed-documents review-page${attorney ? " attorney-review-page" : ""}${embedded ? " is-embedded" : ""}`}
    >
      {attorney ? (
        <header className="attorney-matter-header">
          <div className="attorney-matter-toolbar">
            <Link className="rv-backlink" to="/attorney/reviews">
              <ArrowLeft size={13} /> All reviews
            </Link>
            <Button
              variant="link"
              disabled={busy}
              onClick={() => action(() => load())}
            >
              <RefreshCw size={12} /> Refresh
            </Button>
          </div>
          <div className="attorney-matter-title">
            <h1 title={r.title}>{r.title}</h1>
            <div className="review-header-actions">
              <span
                className={`review-badge next-${nextActionTone({ ...r, next_action: next })}`}
              >
                {nextActionLabel({ ...r, next_action: next })}
              </span>
            </div>
          </div>
          <dl className="attorney-matter-meta">
            <div>
              <dt>Client</dt>
              <dd>{r.client_name}</dd>
            </div>
            <div>
              <dt>Practice</dt>
              <dd>{topicLabel(r.topic_key)}</dd>
            </div>
            <div>
              <dt>
                {r.assigned_coverage ? "Assigned jurisdiction" : "Jurisdiction"}
              </dt>
              <dd>
                {jurisdictionLabel(
                  r.assigned_coverage?.jurisdiction || r.jurisdiction
                )}
              </dd>
            </div>
            <div>
              <dt>Submitted</dt>
              <dd>{shortDate(r.created_at)}</dd>
            </div>
            {r.due_at && !r.completed_at && (
              <div>
                <dt>{isOverdue(r) ? "Overdue since" : "Due"}</dt>
                <dd className={isOverdue(r) ? "review-overdue" : undefined}>
                  {dateLabel(r.due_at)}
                </dd>
              </div>
            )}
          </dl>
        </header>
      ) : embedded ? null : (
        <>
          <Link className="rv-backlink" to="/questions">
            <ArrowLeft size={13} /> My questions
          </Link>
          <header className="reviewed-documents-heading review-detail-header">
            <div>
              <p className="reviewed-eyebrow">ATTORNEY REVIEW</p>
              <h1>{r.title}</h1>
              <div className="review-detail-heading-meta">
                <span>{topicLabel(r.topic_key)}</span>
                <span>·</span>
                <MapPin size={12} />
                {jurisdictionLabel(r.jurisdiction)}
              </div>
            </div>
            <span className={`review-badge ${r.status}`}>
              {customerStatusLabel(r)}
            </span>
          </header>
        </>
      )}
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      {attorney && workflowControls}
      {attorney ? (
        <AttorneyWorkspace
          ref={attorneyWorkspace}
          key={id}
          hasDocuments={submittedItems(data.snapshot, addedFiles).length > 0}
          canRevise={revisionAllowed}
          closed={ended}
          answerCount={data.answers.length}
          record={snapshotSection}
          discussion={discussionSection}
          draft={draftSection}
          answers={answersSection}
          tab={activeTab}
          onTabChange={setWorkTab}
          messageCount={messages.length}
          clientWaiting={next === "reply"}
          hasDraft={!!draft.trim()}
        />
      ) : (
        <div className="review-detail-grid client-review-grid">
          <div className="review-detail-main">
            {workflowControls}
            <div
              className="review-tabs"
              role="tablist"
              aria-label="Review sections"
            >
              {[
                [
                  "answers",
                  data.answers.length
                    ? `Attorney’s answer (${data.answers.length})`
                    : "Attorney’s answer",
                ],
                ["discussion", "Messages"],
                ["snapshot", "What the attorney sees"],
              ].map(([key, label]) => (
                <Button
                  key={key}
                  role="tab"
                  aria-selected={tab === key}
                  variant={tab === key ? "dark" : "light"}
                  onClick={() => setTab(key)}
                >
                  {label}
                </Button>
              ))}
            </div>
            {tab === "discussion" && discussionSection}
            {tab === "snapshot" && snapshotSection}
            {tab === "answers" && answersSection}
          </div>
          <aside className="review-sidebar" aria-label="Review details">
            <h2>Your attorney</h2>
            <div className="review-participant">
              <span className="rv-avatar" aria-hidden="true">
                {initials(r.attorney_name || "Awaiting")}
              </span>
              <div>
                <strong>{r.attorney_name || "Finding your attorney"}</strong>
                <small>
                  {r.attorney_name
                    ? "Your attorney"
                    : `We’ll match you with an attorney who covers ${topicLabel(
                        r.topic_key
                      ).toLowerCase()}.`}
                </small>
              </div>
            </div>
            <ol className="review-steps" aria-label="Review progress">
              {(r.status === "cancelled"
                ? ["Request sent", "Request ended"]
                : [
                    "Request sent",
                    "Attorney matched",
                    "Attorney working",
                    "Answered",
                    "Closed",
                  ]
              ).map((label, i) => {
                const steps: Record<string, number> = {
                  queued: 0,
                  assigned: 1,
                  in_progress: 2,
                  completed: 3,
                };
                const step =
                  r.status === "cancelled"
                    ? 1
                    : r.closed_at
                      ? 4
                      : (steps[r.status] ?? 0);
                const current = i === step;
                return (
                  <li
                    key={label}
                    className={`${i <= step ? "done" : ""}${current ? " current" : ""}`}
                    aria-current={current ? "step" : undefined}
                  >
                    <span>{i <= step ? <Check size={10} /> : i + 1}</span>
                    <div className="review-step-content">
                      <strong>
                        {i === 4 && r.status === "cancelled"
                          ? "Request ended"
                          : label}
                      </strong>
                      {i === 0 && <small>{shortDate(r.created_at)}</small>}
                    </div>
                  </li>
                );
              })}
            </ol>
            <p className="review-sidebar-note">
              <FileText size={13} className="me-1" /> The attorney sees your AI
              chat and files as they were when you submitted. Later AI-chat
              messages are not added. Messages in this review are shared
              directly with your attorney.
            </p>
            {onBackToChat ? (
              <Button variant="link" className="p-0" onClick={onBackToChat}>
                <ArrowLeft size={12} /> Back to the AI chat
              </Button>
            ) : (
              <Link to={`/questions/${r.question_id}`}>
                <ArrowLeft size={12} /> Back to the AI chat
              </Link>
            )}
          </aside>
        </div>
      )}
      <Modal
        show={showDelivery}
        onHide={() => !busy && setShowDelivery(false)}
        centered
        scrollable
      >
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Deliver this answer?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            The client is notified and sees this answer as delivered.
            {r.clarification_round_limit != null || data.proposals?.length
              ? " They can ask follow-up questions within the agreed terms."
              : ""}{" "}
            You can deliver a revised answer later; earlier versions stay
            available.
          </p>
          {lint.blocking.length > 0 && (
            <Alert variant="danger">
              <strong>Fix before delivering:</strong>
              <ul className="mb-0">
                {lint.blocking.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Alert>
          )}
          {lint.warnings.length > 0 && (
            <Alert variant="warning">
              <strong>Check before delivering:</strong>
              <ul className="mb-0">
                {lint.warnings.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Alert>
          )}
          <h3 className="h6">Client preview</h3>
          <div className="answer-delivery-preview">
            <ReviewMarkdown>{savedDraft}</ReviewMarkdown>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={busy}
            onClick={() => setShowDelivery(false)}
          >
            Keep drafting
          </Button>
          <Button
            disabled={busy || dirty || lint.blocking.length > 0}
            onClick={deliver}
          >
            {pendingAction === "deliver" ? "Delivering…" : "Confirm delivery"}
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}
